import { useState, useEffect, Suspense, lazy } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

// URL et clé Supabase pour requêtes directes (contourne le cache de types)
const SUPABASE_URL = "https://apmnhnunfrcbydwysfez.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFwbW5obnVuZnJjYnlkd3lzZmV6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTc3NTg3MjAsImV4cCI6MjA3MzMzNDcyMH0._6Rb9fMH3NB5rVElNAXaa3djas5mccVIGFO18DAlroY";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Save, 
  Eye, 
  EyeOff, 
  Globe,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';

// Lazy import pour éviter les problèmes SSR
const ReactQuill = lazy(() => import('react-quill'));

interface StaticPage {
  id: string;
  slug: string;
  title_fr: string;
  title_en: string;
  content_fr: string;
  content_en: string;
  meta_description_fr: string | null;
  meta_description_en: string | null;
  is_published: boolean;
  show_in_footer: boolean;
  sort_order: number;
  updated_at: string;
}

interface LegalPageEditorProps {
  slug: 'terms' | 'privacy';
  pageTitle: string;
}

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link'
];

export function LegalPageEditor({ slug, pageTitle }: LegalPageEditorProps) {
  const [page, setPage] = useState<StaticPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editLang, setEditLang] = useState<'fr' | 'en'>('fr');
  const [showPreview, setShowPreview] = useState(false);
  const [isNewPage, setIsNewPage] = useState(false);
  const { toast } = useToast();
  const { language } = useLanguage();

  // Default content for new pages
  const getDefaultContent = (pageSlug: string) => {
    if (pageSlug === 'terms') {
      return {
        title_fr: 'Conditions Générales d\'Utilisation',
        title_en: 'Terms of Service',
        content_fr: `<h2>1. Objet</h2>
<p>Les présentes Conditions Générales d'Utilisation (CGU) ont pour objet de définir les modalités d'utilisation de la plateforme Nazandoo.</p>

<h2>2. Acceptation</h2>
<p>L'utilisation de la Plateforme implique l'acceptation pleine et entière des présentes CGU.</p>

<h2>3. Services</h2>
<p>Nazandoo propose une marketplace permettant la mise en relation de vendeurs et d'acheteurs.</p>`,
        content_en: `<h2>1. Purpose</h2>
<p>These Terms of Service (ToS) define the terms of use of the Nazandoo platform.</p>

<h2>2. Acceptance</h2>
<p>Using the Platform implies full acceptance of these ToS.</p>

<h2>3. Services</h2>
<p>Nazandoo offers a marketplace connecting sellers and buyers.</p>`,
      };
    } else if (pageSlug === 'legal') {
      return {
        title_fr: 'Mentions Légales',
        title_en: 'Legal Notice',
        content_fr: `<h2>1. Informations sur l'Éditeur</h2>
<p><strong>Nom de la plateforme :</strong> Nazandoo<br/>
<strong>Raison sociale :</strong> Nazandoo SARL<br/>
<strong>Siège social :</strong> Kinshasa, République Démocratique du Congo<br/>
<strong>Email :</strong> contact@nazandoo.com<br/>
<strong>Téléphone :</strong> +243 XXX XXX XXX<br/>
<strong>Numéro d'enregistrement :</strong> [À compléter]</p>

<h2>2. Hébergement de la Plateforme</h2>
<p>La plateforme Nazandoo est hébergée par :<br/>
<strong>Hébergeur :</strong> Supabase Inc.<br/>
<strong>Adresse :</strong> San Francisco, CA, United States<br/>
<strong>Site web :</strong> <a href="https://supabase.com" target="_blank" rel="noopener">https://supabase.com</a></p>

<h2>3. Description des Services</h2>
<p>Nazandoo est une plateforme de marketplace e-commerce qui met en relation des vendeurs et des acheteurs en République Démocratique du Congo. Nos services incluent :</p>
<ul>
  <li><strong>Marketplace :</strong> Mise en relation entre vendeurs professionnels et acheteurs</li>
  <li><strong>Système de paiement :</strong> Facilitation des transactions via cartes bancaires et Mobile Money</li>
  <li><strong>Points relais :</strong> Réseau de points de collecte pour retrait des commandes</li>
  <li><strong>Gestion des commandes :</strong> Suivi en temps réel des commandes avec système QR code</li>
  <li><strong>Programme vendeur :</strong> Outils pour créer et gérer une boutique en ligne</li>
</ul>

<h2>4. Propriété Intellectuelle</h2>
<p>L'ensemble du contenu présent sur la plateforme Nazandoo (textes, images, logos, graphismes, vidéos, sons, bases de données, logiciels, etc.) est protégé par les lois relatives à la propriété intellectuelle.</p>
<p>Le nom "Nazandoo", le logo et l'ensemble des marques associées sont des marques déposées. Toute reproduction, représentation, modification, publication ou adaptation de tout ou partie des éléments de la plateforme, quel que soit le moyen ou le procédé utilisé, est interdite sans autorisation écrite préalable.</p>
<p>Toute exploitation non autorisée de la plateforme ou de l'un des éléments qu'elle contient sera considérée comme constitutive d'une contrefaçon et poursuivie conformément aux dispositions légales en vigueur.</p>

<h2>5. Protection des Données Personnelles</h2>
<p>Conformément aux réglementations sur la protection des données personnelles, Nazandoo s'engage à protéger la vie privée de ses utilisateurs. Les données collectées sont traitées de manière sécurisée et ne sont jamais vendues à des tiers.</p>
<p>Pour plus d'informations sur la collecte, l'utilisation et la protection de vos données personnelles, veuillez consulter notre <a href="/privacy">Politique de Confidentialité</a>.</p>
<p>Vous disposez d'un droit d'accès, de rectification, de suppression et de portabilité de vos données personnelles. Pour exercer ces droits, contactez-nous à : <strong>privacy@nazandoo.com</strong></p>

<h2>6. Responsabilité</h2>
<p>Nazandoo agit en tant qu'intermédiaire technique entre vendeurs et acheteurs. La plateforme ne peut être tenue responsable :</p>
<ul>
  <li>De la qualité, de la conformité ou de la disponibilité des produits vendus par les vendeurs</li>
  <li>Des litiges entre acheteurs et vendeurs concernant les transactions</li>
  <li>Des dommages directs ou indirects résultant de l'utilisation de la plateforme</li>
  <li>Des interruptions temporaires ou permanentes du service pour maintenance ou cas de force majeure</li>
</ul>
<p>Nazandoo s'efforce néanmoins de vérifier l'identité des vendeurs et de maintenir un niveau de sécurité élevé sur la plateforme.</p>

<h2>7. Droit Applicable et Juridiction</h2>
<p>Les présentes mentions légales sont régies par le droit congolais. En cas de litige relatif à l'interprétation ou à l'exécution des présentes, et à défaut d'accord amiable, les tribunaux de Kinshasa seront seuls compétents.</p>

<h2>8. Contact et Réclamations</h2>
<p>Pour toute question concernant les présentes mentions légales ou pour signaler un contenu illicite présent sur la plateforme :</p>
<p><strong>Email général :</strong> contact@nazandoo.com<br/>
<strong>Support technique :</strong> support@nazandoo.com<br/>
<strong>Protection des données :</strong> privacy@nazandoo.com<br/>
<strong>Signalement d'abus :</strong> abuse@nazandoo.com</p>

<h2>9. Modifications</h2>
<p>Nazandoo se réserve le droit de modifier les présentes mentions légales à tout moment. Les utilisateurs seront informés de toute modification substantielle par email ou via une notification sur la plateforme.</p>`,
        content_en: `<h2>1. Publisher Information</h2>
<p><strong>Platform name:</strong> Nazandoo<br/>
<strong>Company name:</strong> Nazandoo SARL<br/>
<strong>Headquarters:</strong> Kinshasa, Democratic Republic of Congo<br/>
<strong>Email:</strong> contact@nazandoo.com<br/>
<strong>Phone:</strong> +243 XXX XXX XXX<br/>
<strong>Registration number:</strong> [To be completed]</p>

<h2>2. Platform Hosting</h2>
<p>The Nazandoo platform is hosted by:<br/>
<strong>Host:</strong> Supabase Inc.<br/>
<strong>Address:</strong> San Francisco, CA, United States<br/>
<strong>Website:</strong> <a href="https://supabase.com" target="_blank" rel="noopener">https://supabase.com</a></p>

<h2>3. Service Description</h2>
<p>Nazandoo is an e-commerce marketplace platform connecting sellers and buyers in the Democratic Republic of Congo. Our services include:</p>
<ul>
  <li><strong>Marketplace:</strong> Connection between professional sellers and buyers</li>
  <li><strong>Payment system:</strong> Transaction facilitation via bank cards and Mobile Money</li>
  <li><strong>Relay points:</strong> Network of collection points for order pickup</li>
  <li><strong>Order management:</strong> Real-time order tracking with QR code system</li>
  <li><strong>Vendor program:</strong> Tools to create and manage an online store</li>
</ul>

<h2>4. Intellectual Property</h2>
<p>All content on the Nazandoo platform (texts, images, logos, graphics, videos, sounds, databases, software, etc.) is protected by intellectual property laws.</p>
<p>The name "Nazandoo", the logo and all associated trademarks are registered trademarks. Any reproduction, representation, modification, publication or adaptation of all or part of the platform elements, regardless of the means or process used, is prohibited without prior written authorization.</p>
<p>Any unauthorized use of the platform or any elements it contains will be considered as constituting counterfeiting and prosecuted in accordance with applicable legal provisions.</p>

<h2>5. Personal Data Protection</h2>
<p>In accordance with data protection regulations, Nazandoo is committed to protecting user privacy. Collected data is processed securely and never sold to third parties.</p>
<p>For more information on the collection, use and protection of your personal data, please consult our <a href="/privacy">Privacy Policy</a>.</p>
<p>You have the right to access, rectify, delete and port your personal data. To exercise these rights, contact us at: <strong>privacy@nazandoo.com</strong></p>

<h2>6. Liability</h2>
<p>Nazandoo acts as a technical intermediary between sellers and buyers. The platform cannot be held responsible for:</p>
<ul>
  <li>The quality, compliance or availability of products sold by vendors</li>
  <li>Disputes between buyers and sellers regarding transactions</li>
  <li>Direct or indirect damages resulting from the use of the platform</li>
  <li>Temporary or permanent service interruptions for maintenance or force majeure</li>
</ul>
<p>Nevertheless, Nazandoo strives to verify seller identity and maintain a high level of security on the platform.</p>

<h2>7. Applicable Law and Jurisdiction</h2>
<p>This legal notice is governed by Congolese law. In case of dispute relating to the interpretation or execution of these terms, and failing an amicable agreement, the courts of Kinshasa will have sole jurisdiction.</p>

<h2>8. Contact and Complaints</h2>
<p>For any questions regarding this legal notice or to report illegal content on the platform:</p>
<p><strong>General email:</strong> contact@nazandoo.com<br/>
<strong>Technical support:</strong> support@nazandoo.com<br/>
<strong>Data protection:</strong> privacy@nazandoo.com<br/>
<strong>Abuse reporting:</strong> abuse@nazandoo.com</p>

<h2>9. Modifications</h2>
<p>Nazandoo reserves the right to modify this legal notice at any time. Users will be informed of any substantial modification by email or via a notification on the platform.</p>`,
      };
    } else {
      return {
        title_fr: 'Politique de Confidentialité',
        title_en: 'Privacy Policy',
        content_fr: `<h2>1. Introduction</h2>
<p>Nazandoo s'engage à protéger la vie privée de ses utilisateurs.</p>

<h2>2. Données collectées</h2>
<p>Nous collectons les données nécessaires au fonctionnement de nos services.</p>

<h2>3. Utilisation</h2>
<p>Vos données sont utilisées pour gérer votre compte et traiter vos commandes.</p>`,
        content_en: `<h2>1. Introduction</h2>
<p>Nazandoo is committed to protecting user privacy.</p>

<h2>2. Data Collected</h2>
<p>We collect data necessary for our services.</p>

<h2>3. Usage</h2>
<p>Your data is used to manage your account and process orders.</p>`,
      };
    }
  };

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      setIsNewPage(false);
      
      console.log('🔍 Fetching page with RPC (JSONB):', slug);
      
      // Utiliser RPC qui retourne JSONB (mieux géré par PostgREST)
      const { data, error } = await supabase
        .rpc('get_static_page', { page_slug: slug });
      
      console.log('📦 RPC JSONB Data received:', data);
      console.log('❌ RPC Error:', error);
      
      if (error) {
        console.error('❌ RPC Error:', error);
        throw error;
      }
      
      // data est maintenant un objet JSONB, pas un array
      if (data && Object.keys(data).length > 0) {
        console.log('✅ Page trouvée via RPC JSONB, ID:', data.id);
        setPage(data as StaticPage);
        setIsNewPage(false);
      } else {
        console.log('⚠️ Aucune donnée retournée - création d\'une nouvelle page');
        // Page doesn't exist yet - show form to create it
        const defaults = getDefaultContent(slug);
        setPage({
          id: '',
          slug,
          title_fr: defaults.title_fr,
          title_en: defaults.title_en,
          content_fr: defaults.content_fr,
          content_en: defaults.content_en,
          meta_description_fr: null,
          meta_description_en: null,
          is_published: true,
          show_in_footer: true,
          sort_order: slug === 'terms' ? 1 : 2,
          updated_at: new Date().toISOString()
        });
        setIsNewPage(true);
      }
    } catch (error) {
      console.error('Error fetching page:', error);
      // Still show form with defaults so user can create the page
      const defaults = getDefaultContent(slug);
      setPage({
        id: '',
        slug,
        title_fr: defaults.title_fr,
        title_en: defaults.title_en,
        content_fr: defaults.content_fr,
        content_en: defaults.content_en,
        meta_description_fr: null,
        meta_description_en: null,
        is_published: true,
        show_in_footer: true,
        sort_order: slug === 'terms' ? 1 : 2,
        updated_at: new Date().toISOString()
      });
      setIsNewPage(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!page) return;

    try {
      setSaving(true);
      
      if (page.id) {
        // Update existing via RPC (retourne JSONB)
        const { data, error } = await supabase.rpc('update_static_page', {
          page_id: page.id,
          new_title_fr: page.title_fr,
          new_title_en: page.title_en,
          new_content_fr: page.content_fr,
          new_content_en: page.content_en,
          new_meta_fr: page.meta_description_fr,
          new_meta_en: page.meta_description_en,
          new_is_published: page.is_published,
          new_show_in_footer: page.show_in_footer,
          new_sort_order: page.sort_order
        });

        if (error) throw error;
        
        // data est un objet JSONB, pas un array
        if (data && Object.keys(data).length > 0) {
          setPage(data as StaticPage);
        }
        
        toast({
          title: "Succès",
          description: "Page mise à jour avec succès"
        });
      } else {
        // Insert new - use direct query since RPC for insert is more complex
        const { data, error } = await supabase
          .from('static_pages')
          .insert({
            slug: page.slug,
            title_fr: page.title_fr,
            title_en: page.title_en,
            content_fr: page.content_fr,
            content_en: page.content_en,
            meta_description_fr: page.meta_description_fr,
            meta_description_en: page.meta_description_en,
            is_published: page.is_published,
            show_in_footer: page.show_in_footer,
            sort_order: page.sort_order
          })
          .select()
          .single();

        if (error) throw error;
        
        if (data) {
          setPage(data as StaticPage);
        }
        setIsNewPage(false);
        
        toast({
          title: "Succès",
          description: "Page créée avec succès ! Elle est maintenant accessible sur le site."
        });
      }
      
      fetchPage();
    } catch (error: any) {
      console.error('Error saving page:', error);
      
      toast({
        title: "Erreur",
        description: error?.message || "Impossible de sauvegarder la page",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof StaticPage, value: any) => {
    if (!page) return;
    setPage({ ...page, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        <p>Une erreur est survenue lors du chargement.</p>
        <Button onClick={fetchPage} variant="outline" className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isNewPage && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          <p className="font-medium">📝 Cette page n'existe pas encore dans la base de données.</p>
          <p className="text-sm mt-1">Modifiez le contenu ci-dessous puis cliquez sur "Créer la page" pour la sauvegarder.</p>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{pageTitle}</h3>
          <p className="text-sm text-muted-foreground">
            {isNewPage ? 'Créez cette page légale' : 'Modifiez le contenu de cette page légale'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isNewPage && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`/${slug}`, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Voir la page
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Enregistrement...' : isNewPage ? 'Créer la page' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {/* Publication settings */}
      <div className="flex flex-wrap gap-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-3">
          <Switch
            checked={page.is_published}
            onCheckedChange={(checked) => updateField('is_published', checked)}
          />
          <div>
            <Label className="font-medium">Page publiée</Label>
            <p className="text-xs text-muted-foreground">Visible par les visiteurs</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={page.show_in_footer}
            onCheckedChange={(checked) => updateField('show_in_footer', checked)}
          />
          <div>
            <Label className="font-medium">Afficher dans le footer</Label>
            <p className="text-xs text-muted-foreground">Lien en bas de page</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {page.is_published ? (
            <Badge variant="default" className="bg-green-500">
              <Eye className="h-3 w-3 mr-1" />
              Publiée
            </Badge>
          ) : (
            <Badge variant="secondary">
              <EyeOff className="h-3 w-3 mr-1" />
              Brouillon
            </Badge>
          )}
        </div>
      </div>

      {/* Language tabs */}
      <Tabs value={editLang} onValueChange={(v) => setEditLang(v as 'fr' | 'en')}>
        <TabsList>
          <TabsTrigger value="fr" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Français
          </TabsTrigger>
          <TabsTrigger value="en" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            English
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fr" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="title_fr">Titre (FR)</Label>
            <Input
              id="title_fr"
              value={page.title_fr}
              onChange={(e) => updateField('title_fr', e.target.value)}
              placeholder="Titre de la page en français"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="meta_fr">Meta description (FR)</Label>
            <Input
              id="meta_fr"
              value={page.meta_description_fr || ''}
              onChange={(e) => updateField('meta_description_fr', e.target.value)}
              placeholder="Description pour le SEO"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Contenu (FR)</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? 'Éditer' : 'Aperçu'}
              </Button>
            </div>
            {showPreview ? (
              <div 
                className="border rounded-lg p-6 bg-white prose prose-sm max-w-none min-h-[400px]"
                dangerouslySetInnerHTML={{ __html: page.content_fr }}
              />
            ) : (
              <Suspense fallback={<div className="h-[400px] bg-muted animate-pulse rounded-lg" />}>
                <ReactQuill
                  theme="snow"
                  value={page.content_fr}
                  onChange={(value) => updateField('content_fr', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  className="bg-white rounded-lg"
                  style={{ height: '350px', marginBottom: '50px' }}
                />
              </Suspense>
            )}
          </div>
        </TabsContent>

        <TabsContent value="en" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="title_en">Title (EN)</Label>
            <Input
              id="title_en"
              value={page.title_en}
              onChange={(e) => updateField('title_en', e.target.value)}
              placeholder="Page title in English"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="meta_en">Meta description (EN)</Label>
            <Input
              id="meta_en"
              value={page.meta_description_en || ''}
              onChange={(e) => updateField('meta_description_en', e.target.value)}
              placeholder="Description for SEO"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Content (EN)</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? 'Edit' : 'Preview'}
              </Button>
            </div>
            {showPreview ? (
              <div 
                className="border rounded-lg p-6 bg-white prose prose-sm max-w-none min-h-[400px]"
                dangerouslySetInnerHTML={{ __html: page.content_en }}
              />
            ) : (
              <Suspense fallback={<div className="h-[400px] bg-muted animate-pulse rounded-lg" />}>
                <ReactQuill
                  theme="snow"
                  value={page.content_en}
                  onChange={(value) => updateField('content_en', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  className="bg-white rounded-lg"
                  style={{ height: '350px', marginBottom: '50px' }}
                />
              </Suspense>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default LegalPageEditor;
